import { jsx as _jsx } from "nativewind/jsx-runtime";
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import styled from 'styled-components/native';
import ItemMapView from '@/components/maps/ItemMapView';
import { toPlaceMarkerItem } from '@/components/maps/MarkerItem';
import { color } from '@/constant/color';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import { draftCameraRegionAtom, searchQueryAtom, toiletLayerActiveAtom, viewStateAtom, } from '@/screens/SearchScreen/atoms';
import SearchItemCard from '@/screens/SearchScreen/components/SearchItemCard';
import useToiletLayerQuery from '@/screens/SearchScreen/useToiletLayerQuery';
import ToiletCard from '@/components/toilet/ToiletCard';
const SearchMapView = forwardRef(({ data, resultMode, onRefresh, AboveCardsSlot }, ref) => {
    const [toiletLayerActive, setToiletLayerActive] = useAtom(toiletLayerActiveAtom);
    const toiletLayerData = useToiletLayerQuery();
    // 화장실 pin 클릭 시 임시 포커스 (history stack에 쌓이지 않음)
    const [overlayFocusedItem, setOverlayFocusedItem] = useState(null);
    useImperativeHandle(ref, () => ({
        fitToItems: (_items) => {
            var _a;
            (_a = mapViewRef.current) === null || _a === void 0 ? void 0 : _a.fitToItems(_items);
        },
        moveToItem: (targetId) => {
            var _a;
            const target = datasForUI === null || datasForUI === void 0 ? void 0 : datasForUI.find(it => it.id === targetId);
            if (!target)
                return;
            (_a = mapViewRef.current) === null || _a === void 0 ? void 0 : _a.moveToItem(target);
        },
        clearOverlayFocus: () => {
            setOverlayFocusedItem(null);
        },
        hasOverlayFocus: () => overlayFocusedItem != null,
    }));
    const mapViewRef = useRef(null);
    const [searchQuery, _] = useAtom(searchQueryAtom);
    const viewState = useAtomValue(viewStateAtom);
    const setDraftCameraRegion = useSetAtom(draftCameraRegionAtom);
    const datasForUI = useMemo(() => {
        var _a, _b;
        if (resultMode === 'toilet') {
            // Toilet data is already mapped with MarkerItem
            return (_a = data) !== null && _a !== void 0 ? _a : [];
        }
        // Place data needs to be mapped
        return (_b = data === null || data === void 0 ? void 0 : data.map(toPlaceMarkerItem)) !== null && _b !== void 0 ? _b : [];
    }, [data, resultMode]);
    const isSearchQueryEmpty = !searchQuery.text;
    const showToiletLayerToggle = resultMode !== 'toilet';
    const handleOverlayMarkerPress = useCallback((item) => {
        const toiletItem = toiletLayerData.find(t => t.id === item.id);
        if (toiletItem) {
            setOverlayFocusedItem(toiletItem);
        }
    }, [toiletLayerData]);
    const handleOverlayDismiss = useCallback(() => {
        setOverlayFocusedItem(null);
    }, []);
    const handleToiletLayerToggle = useCallback(() => {
        const newActive = !toiletLayerActive;
        setToiletLayerActive(newActive);
        if (!newActive) {
            setOverlayFocusedItem(null);
        }
    }, [toiletLayerActive, setToiletLayerActive]);
    return (_jsx(LogParamsProvider, { params: { search_view_mode: 'map' }, children: _jsx(Wrapper, { children: _jsx(ItemMapView, { ref: mapViewRef, items: datasForUI, onCameraIdle: region => {
                    // viewState 가 map 이 아닐 때는 제대로된 region 이 업데이트 되지 않을 수 있다
                    // (by 모바일 키보드로 인한 지도 resizing 등...)
                    if (viewState.type === 'map' && !viewState.inputMode) {
                        setDraftCameraRegion(region);
                    }
                }, onRefresh: onRefresh, isRefreshVisible: !isSearchQueryEmpty, ItemCard: (resultMode === 'toilet'
                    ? ToiletCard
                    : SearchItemCard), 
                // 화장실 레이어 overlay
                overlayMarkers: showToiletLayerToggle && toiletLayerActive
                    ? toiletLayerData
                    : undefined, overlayFocusedItem: overlayFocusedItem, onOverlayMarkerPress: handleOverlayMarkerPress, onOverlayDismiss: handleOverlayDismiss, OverlayItemCard: ToiletCard, 
                // 토글 버튼
                toiletLayerActive: toiletLayerActive, onToiletLayerToggle: handleToiletLayerToggle, showToiletLayerToggle: showToiletLayerToggle, AboveCardsSlot: AboveCardsSlot }) }) }));
});
export default SearchMapView;
const Wrapper = styled.View `
  background-color: ${() => color.white};
  flex-grow: 1;
  overflow: hidden;
`;
